import * as React from "react";
import { MenuItem, Select, SelectChangeEvent, styled, Theme } from "@mui/material";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useTranslation } from "react-i18next";

interface PrioritySelectProps {
  value: "low" | "medium" | "high" | "urgent";
  onChange: (priority: "low" | "medium" | "high" | "urgent") => void;
}

const PrioritySelect: React.FC<PrioritySelectProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e.target.value as "low" | "medium" | "high" | "urgent");
  };

  return (
    <CustomSelect
      sx={{ fontWeight: 500 }}
      IconComponent={ExpandMoreRoundedIcon}
      size="small"
      MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
      value={value}
      onChange={handleSelectChange}
    >
      <CustomMenuItem value="low">
        <FlagOutlinedIcon sx={{ mr: 0.5 }} />
        {t("common:low")}
      </CustomMenuItem>
      <CustomMenuItem value="medium">
        <FlagOutlinedIcon sx={{ mr: 0.5 }} />
        {t("common:medium")}
      </CustomMenuItem>
      <CustomMenuItem value="high">
        <FlagOutlinedIcon sx={{ mr: 0.5 }} />
        {t("common:high")}
      </CustomMenuItem>
      <CustomMenuItem value="urgent">
        <FlagOutlinedIcon sx={{ mr: 0.5 }} />
        {t("common:urgent")}
      </CustomMenuItem>
    </CustomSelect>
  );
};

export default PrioritySelect;

const getColorScheme = ({
  theme,
  value,
}: { theme: Theme } & Pick<PrioritySelectProps, "value">): { main: string; alpha?: string } => {
  switch (value) {
    case "low":
      return {
        main: theme.palette.info.main,
        alpha: theme.palette.info.alpha,
      };
    case "medium":
      return {
        main: theme.palette.warning.main,
        alpha: theme.palette.warning.alpha,
      };
    case "high":
      return {
        main: theme.palette.error.main,
        alpha: theme.palette.error.alpha,
      };
    case "urgent":
      return {
        main: theme.palette.primary.main,
        alpha: theme.palette.primary.alpha,
      };
  }
};

const CustomSelect = styled(Select)<Pick<PrioritySelectProps, "value">>(({ theme, value }) => {
  const colorScheme = getColorScheme({ theme, value });

  return {
    padding: 0,
    height: "24px",
    border: "none",
    color: colorScheme.main,
    background: colorScheme.alpha,
    "&:hover": {
      background: colorScheme.alpha,
    },
    ".MuiSvgIcon-root": {
      color: colorScheme.main,
      right: theme.spacing(0.5),
    },
    ".MuiSelect-select": {
      padding: theme.spacing(0.5),
      paddingRight: `${theme.spacing(3)} !important`,
    },

    ".MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  };
});

const CustomMenuItem = styled(MenuItem)(() => ({
  ".MuiSvgIcon-root": {
    display: "none",
  },
}));
